import { useRef, useState } from 'react';
import type { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Avatar, Box, Button, ButtonBase, Divider, Popover, Typography } from '@mui/material';
import useAuth from 'src/hooks/useAuth';
import { Logger } from 'src/utils/logger';
import useVendor from 'src/hooks/useVendor';

const AccountPopover: FC<React.PropsWithChildren<unknown>> = () => {
  const anchorRef = useRef<HTMLButtonElement | null>(null);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState<boolean>(false);
  const { vendor } = useVendor();

  const handleOpen = (): void => {
    setOpen(true);
  };

  const handleClose = (): void => {
    setOpen(false);
  };

  const handleLogout = async (): Promise<void> => {
    try {
      handleClose();
      await logout();
      navigate('/');
    } catch (err) {
      Logger.error(err);
      toast.error('Unable to logout.');
    }
  };

  const clearTourCookie = () => {
    document.cookie = 'dashboardNavTour_VP_ShowViewed=; expires = 0';
    document.cookie = 'userAdminTour_VP_ShowViewed=; expires = 0';
    document.cookie = 'vendorBoothTour_VP_ShowViewed=; expires = 0';

    //refresh page
    window.location.reload();
  };

  return (
    <div className="global-header__account-pop-over" id="global-header__account-pop-over">
      <Box
        component={ButtonBase}
        onClick={handleOpen}
        ref={anchorRef}
        sx={{
          alignItems: 'center',
          display: 'flex',
        }}
      >
        <Avatar
          sx={{
            height: 32,
            width: 32,
          }}
        />
      </Box>
      <Popover
        anchorEl={anchorRef.current}
        anchorOrigin={{
          horizontal: 'center',
          vertical: 'bottom',
        }}
        keepMounted
        onClose={handleClose}
        open={open}
        PaperProps={{
          sx: { width: 240 },
        }}
      >
        <Box sx={{ p: 2 }}>
          <Typography color="textPrimary" variant="subtitle2">
            {user.name}
          </Typography>
        </Box>
        <Divider />

        <Box sx={{ mb: 2, pl: 2, pr: 2, mt: 1 }}>
          <Typography color="textPrimary" variant="subtitle2">
            Category Managers
          </Typography>
          {vendor?.categoryManagers?.length > 0 &&
            vendor.categoryManagers
              .filter((x) => x.email != null && x.fullName != null)
              .map((categoryManager, i) => {
                const catMangerMailTo = categoryManager.email ? `mailto:${categoryManager.email}` : '';
                return (
                  <div key={i} style={{ padding: '4px' }}>
                    <Typography color="textPrimary" variant="body1" style={{ fontSize: 'small' }}>
                      {categoryManager.fullName} <br /> <a href={catMangerMailTo}>{categoryManager.email}</a>
                    </Typography>
                  </div>
                );
              })}
        </Box>

        <Divider />
        <Box sx={{ p: 2 }}>
          <Button color="primary" fullWidth onClick={clearTourCookie} variant="outlined">
            Retake Tour
          </Button>
        </Box>

        <Divider />
        <Box sx={{ p: 2 }}>
          <Button color="primary" fullWidth onClick={handleLogout} variant="outlined">
            Logout
          </Button>
        </Box>
      </Popover>
    </div>
  );
};

export default AccountPopover;
